import { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, CircularProgress } from '@mui/material';
import { useDebouncedCallback } from 'use-debounce';
import validator from 'validator';

import UrlTextField from '~/components/atoms/UrlTextField';
import Accordion from '~/components/ui/Accordion/Accordion';
import AsyncSwitch from '~/components/ui/AsyncSwitch';
import { Text } from '~/components/ui/text';
import TextInput from '~/components/ui/TextInput';
import { REDIRECT_QR_URI } from '~/constants/common';
import { useAppDispatch } from '~/hooks/useRedux';
import CreateQRContentBlockLayout from '~/pages/CreateQR/components/CreateQRContentBlockLayout/CreateQRContentBlockLayout';
import { updateQrCodeThunk } from '~/store/reducers/qr-code';
import { COLORS } from '~/styles/colors';

const defaultOrigin = `${REDIRECT_QR_URI}/s/`;

type UrlValidationParams = {
  errorMessage?: string;
  resetErrorMessage?: () => void;
  cancelUrlValidationRequest?: () => void;
  sendUrl?: () => void;
  isProcessing?: boolean;
};

type SettingsBlockProps = {
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  urlValidationParams?: UrlValidationParams;
  scanCount?: string;
  isAutogeneratedUrl?: boolean;
  isLimitsEnabled?: boolean;
  defaultUrlValue?: string;
  urlFromBE?: string;
  isEditPage?: boolean;
};
export function SettingsBlock({
  onChange,
  urlValidationParams: { errorMessage, resetErrorMessage, cancelUrlValidationRequest, sendUrl, isProcessing },
  scanCount,
  isAutogeneratedUrl,
  isLimitsEnabled,
  defaultUrlValue,
  urlFromBE,
  isEditPage,
}: SettingsBlockProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const validateScan = (input) => {
    if (input === '') {
      return true;
    }
    return validator.isInt(input, { lt: 10000001 });
  };

  const updateScanLimit = useDebouncedCallback(() => {
    if (Number(scanCount) > 0 && Number(scanCount) <= 10000001) {
      dispatch(updateQrCodeThunk({ scanLimit: scanCount }));
    }
  }, 300);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    if (/^(0|[1-9]\d*)?$/.test(value)) {
      onChange({ field: 'scan', value: value });

      updateScanLimit();
    }
  };

  const isUrlEqualCreatedInDB = defaultUrlValue === urlFromBE;
  const alertMessage = (!defaultUrlValue && t('inputs:required')) || (errorMessage && !isUrlEqualCreatedInDB);
  const isAlertUrlValidationShowed = !isAutogeneratedUrl && alertMessage;
  const urlError = (!defaultUrlValue && !isAutogeneratedUrl) || alertMessage;

  const urlHelperText = isAlertUrlValidationShowed ? alertMessage : t('other:maxLength', { maxLength: 25 });
  const showUrlHelperText = isAutogeneratedUrl ? defaultOrigin.length >= 2000 : defaultUrlValue.length >= 25;

  const checkUrl = useDebouncedCallback(() => {
    if (defaultUrlValue !== '' && !isAutogeneratedUrl) {
      sendUrl();
    }
  }, 500);

  return (
    <CreateQRContentBlockLayout type="settings" defaultExpanded={false}>
      <Accordion
        defaultExpanded
        variant="child"
        summary={
          <Text variant="t16Medium">
            {t('template0:title4')}{' '}
            <Text component="span" variant="inputSmall" sx={{ color: COLORS.grey400 }}>
              ({t('template0:subTitle3')})
            </Text>
          </Text>
        }
        details={
          <>
            <AsyncSwitch
              sx={{ marginBottom: '8px' }}
              checked={isAutogeneratedUrl}
              onChange={async (e) => {
                onChange({ field: 'isAutogeneratedUrl', value: e.target.checked });
                if (e.target.checked) {
                  cancelUrlValidationRequest();
                  resetErrorMessage();
                  const res = await dispatch(updateQrCodeThunk({ uri: '', isAutogeneratedUrl: true }));
                  onChange({ field: 'url', value: res.payload.uri });
                } else {
                  dispatch(updateQrCodeThunk({ isAutogeneratedUrl: false }));
                }
              }}
              label={t('template0:sliderTitle1')}
            />
            <UrlTextField
              urlValue={isAutogeneratedUrl ? defaultOrigin : defaultUrlValue}
              disabled={isAutogeneratedUrl}
              withoutUrlValidation={!isAutogeneratedUrl}
              onChange={(e) => {
                onChange({ field: 'url', value: e.target.value });
                checkUrl();
              }}
              placeholder=""
              inputProps={{ maxLength: isAutogeneratedUrl ? 2000 : 25 }}
              helperText={(!isUrlEqualCreatedInDB && errorMessage) || (showUrlHelperText && urlHelperText)}
              fullWidth
              label={t('labels:url')}
              error={isAlertUrlValidationShowed && urlError}
              InputPropsSx={
                !isAutogeneratedUrl
                  ? {
                      '.MuiInputBase-input': {
                        padding: '10px 16px 10px 0',
                        height: '20px',
                      },
                    }
                  : undefined
              }
              InputProps={{
                startAdornment: isAutogeneratedUrl ? undefined : <span>{defaultOrigin}</span>,
                endAdornment: isProcessing && (
                  <Box display="flex" justifyContent="center" alignItems="center">
                    <CircularProgress size={28} />
                  </Box>
                ),
              }}
            />
            {isEditPage && (
              <Alert variant="filled" severity="warning">
                <span style={{ color: 'rgb(244, 241, 241)' }} className="MuiFormHelperText-root">
                  {t('template0:notification')}
                </span>
              </Alert>
            )}
          </>
        }
      />

      <Accordion
        defaultExpanded
        variant="child"
        hideBottomBorder
        summary={
          <Text variant="t16Medium">
            {t('template0:title5')}{' '}
            <Text component="span" variant="inputSmall" sx={{ color: COLORS.grey400 }}>
              ({t('template0:subTitle4')})
            </Text>
          </Text>
        }
        details={
          <>
            <AsyncSwitch
              sx={{ marginBottom: '8px' }}
              checked={isLimitsEnabled}
              onChange={(e) => {
                onChange({ field: 'isLimitsEnabled', value: e.target.checked });
                if (!e.target.checked) {
                  dispatch(updateQrCodeThunk({ scanLimit: null }));
                  onChange({ field: 'scan', value: '' });
                }
              }}
              label={t('template0:sliderTitle2')}
            />

            <TextInput
              disabled={!isLimitsEnabled}
              helperText={
                (!validateScan(scanCount) && isLimitsEnabled && t('errors:scan_limit')) ||
                (isLimitsEnabled && !scanCount && t('inputs:required')) ||
                (scanCount <= 0 && isLimitsEnabled && t('errors:scanMinNumber'))
              }
              error={
                (!validateScan(scanCount) && isLimitsEnabled) ||
                (isLimitsEnabled && !scanCount) ||
                (isLimitsEnabled && scanCount <= 0)
              }
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              value={scanCount}
              onChange={handleChange}
              sx={{ mb: 2 }}
              fullWidth
              defaultValue={scanCount || ''}
            />
          </>
        }
      />
    </CreateQRContentBlockLayout>
  );
}
